import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Badge } from "../../../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "../../../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../ui/tooltip";
import {
  MoreVertical,
  Lightbulb,
  Star,
  Edit,
  Trash2,
  Tag,
  Link as LinkIcon,
  ExternalLink,
  Network,
  X,
  Quote,
  BookOpen,
  User,
  Sparkles,
  Sunrise,
  CheckSquare,
  Globe,
  Check,
} from "lucide-react";
import { InsightCardProps } from "./types";
import { cn } from "../../../../lib/utils";
import { useSpaces } from "../../../../context/SpaceProvider";
import MmLiftMenuItem from "../../../mentalModel/MmLiftMenuItem";
import { useProjectIndex } from "../../../../hooks/useProjectIndex";
import { ProjectChip } from "../ProjectChip";

const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  categories,
  isSelected,
  isFavorite,
  connectionCount = 0,
  onSelect,
  onToggleFavorite,
  onEdit,
  onDelete,
  onAssignCategory,
  onAssignSpace,
  onViewEngagement,
  onViewConnections,
  onConvertToAction,
}) => {
  const { t } = useTranslation();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();

  const getSourceIcon = () => {
    switch (insight.source.type) {
      case "engagement":
        return <BookOpen className="h-3 w-3" />;
      case "mentor_assigned":
        return <User className="h-3 w-3" />;
      case "sketchbook":
        return <Sparkles className="h-3 w-3" />;
      case "reflection":
        return <Sunrise className="h-3 w-3" />;
      default:
        return <Lightbulb className="h-3 w-3" />;
    }
  };

  const getSourceLabel = () => {
    if (insight.source.type === "mentor_assigned") {
      const assignedBy =
        insight.source.assignedByUser?.name ||
        insight.source.assignedByUser?.email ||
        t("mentor");
      return `${t("from")} ${assignedBy}`;
    }
    if (insight.source.type === "engagement") {
      return insight.source.engagementName || insight.source.activityTitle || t("from_engagement");
    }
    if (insight.source.type === "sketchbook") {
      return t("ai_generated") || "AI Generated";
    }
    if (insight.source.type === "reflection") {
      return t("source_reflection") || "Daily reflection";
    }
    return t("personal_note") || "Personal Note";
  };

  const isQuote = insight.content.includes('"') || insight.content.startsWith('"');
  const isArchived = insight.status === "archived";

  return (
    <div
      className={cn(
        "group relative bg-gradient-to-br from-amber-50/50 to-amber-50/20 border rounded-xl p-4 transition-all duration-200",
        isArchived
          ? "opacity-60 border-gray-200 bg-gray-50/50"
          : "border-amber-100 hover:border-amber-300 hover:shadow-md hover:shadow-amber-100/50",
        isSelected && "ring-2 ring-amber-400 border-amber-400"
      )}
    >
      {/* Top Row - Checkbox, Favorite, Menu */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(insight._id)}
            className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleFavorite(insight._id)}
                className={cn(
                  "h-7 w-7 p-0 transition-colors",
                  isFavorite
                    ? "text-amber-500 hover:text-amber-600"
                    : "text-gray-300 hover:text-amber-400"
                )}
              >
                <Star
                  className={cn("h-4 w-4", isFavorite && "fill-current")}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isFavorite ? t("remove_from_favorites") : t("add_to_favorites")}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onEdit(insight)}>
              <Edit className="h-4 w-4 mr-2" />
              {t("edit")}
            </DropdownMenuItem>

            <MmLiftMenuItem entityId={insight._id} />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Tag className="h-4 w-4 mr-2" />
                {t("assign_category")}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => onAssignCategory(insight._id, null)}>
                    <X className="h-4 w-4 mr-2 text-muted-foreground" />
                    {t("no_category")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {categories.map((category) => (
                    <DropdownMenuItem
                      key={category._id}
                      onClick={() => onAssignCategory(insight._id, category._id)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: category.color }}
                        />
                        {category.name}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {onAssignSpace && spaces && spaces.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Globe className="h-4 w-4 mr-2" />
                  {t("assign_to_space") || "Assign to Space"}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {spaces.map((space) => {
                      const isAssigned = insight.spaceIds?.includes(space._id);
                      return (
                        <DropdownMenuItem
                          key={space._id}
                          onClick={() => {
                            const currentIds = insight.spaceIds || [];
                            const newIds = isAssigned
                              ? currentIds.filter((id) => id !== space._id)
                              : [...currentIds, space._id];
                            onAssignSpace(insight._id, newIds);
                          }}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: space.color }}
                            />
                            <span className="flex-1 truncate">
                              {space.name}
                            </span>
                            {isAssigned && (
                              <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}

            {connectionCount > 0 && onViewConnections && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onViewConnections(insight)}>
                  <Network className="h-4 w-4 mr-2" />
                  {t("view_connections") || "View Connections"} ({connectionCount})
                </DropdownMenuItem>
              </>
            )}

            {insight.source.fiProcess && onViewEngagement && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onViewEngagement(insight)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  {t("view_engagement")}
                </DropdownMenuItem>
              </>
            )}

            {onConvertToAction && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onConvertToAction(insight)}>
                  <CheckSquare className="h-4 w-4 mr-2 text-primary" />
                  {t("convert_to_action") || "Convert to Action"}
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete(insight._id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content */}
      <div className="space-y-3">
        {/* Insight Icon & Content */}
        <div className="flex gap-3">
          <div
            className={cn(
              "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center",
              isArchived ? "bg-gray-100" : "bg-amber-100"
            )}
          >
            {isQuote ? (
              <Quote
                className={cn(
                  "h-4 w-4",
                  isArchived ? "text-gray-400" : "text-amber-600"
                )}
              />
            ) : (
              <Lightbulb
                className={cn(
                  "h-4 w-4",
                  isArchived ? "text-gray-400" : "text-amber-600"
                )}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {insight.source.type === "reflection" && insight.source.question && (
              <p className="text-xs text-muted-foreground/70 italic line-clamp-1 mb-1">
                {insight.source.question}
              </p>
            )}
            <p
              className={cn(
                "text-sm leading-relaxed",
                isArchived
                  ? "line-through text-muted-foreground"
                  : "text-gray-700",
                isQuote && "italic"
              )}
            >
              {insight.content}
            </p>
          </div>
        </div>

        {/* Tags/Category/Space Row */}
        <div className="flex flex-wrap items-center gap-1.5">
          {insight.category && (
            <Badge
              variant="outline"
              className="text-xs bg-white/70 hover:bg-white/90 transition-colors cursor-default"
              style={{
                borderColor: insight.category.color,
                color: insight.category.color,
              }}
            >
              <div
                className="w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: insight.category.color }}
              />
              {insight.category.name}
            </Badge>
          )}
          {/* Space badges */}
          {insight.spaceIds && insight.spaceIds.length > 0 && spaces && spaces.map((space) => {
            if (!insight.spaceIds?.includes(space._id)) return null;
            return (
              <Badge
                key={space._id}
                variant="outline"
                className="text-xs bg-white/70 hover:bg-white/90 transition-colors cursor-default"
                style={{
                  borderColor: space.color + "50",
                  color: space.color,
                }}
              >
                <Globe className="w-2.5 h-2.5 mr-1" />
                {space.name}
              </Badge>
            );
          })}
          <ProjectChip item={insight} projectById={projectById} spaces={spaces} variant="badge" />
        </div>

        {/* Footer - Source & Metadata */}
        <div className="flex items-center justify-between pt-2 border-t border-amber-100/50">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              {getSourceIcon()}
              <span className="truncate max-w-[150px]">{getSourceLabel()}</span>
            </div>
            <span className="text-amber-200">•</span>
            <span>
              {insight.createdOn &&
                new Date(insight.createdOn).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
            </span>
          </div>

          <div className="flex items-center gap-1">
            {/* Connection Count */}
            {connectionCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs text-muted-foreground hover:text-amber-600"
                    onClick={() => onViewConnections?.(insight)}
                  >
                    <Network className="h-3 w-3 mr-1" />
                    {connectionCount}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {connectionCount} {t("connected_insights") || "connected insights"}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* External Link */}
            {insight.source.fiProcess && onViewEngagement && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewEngagement(insight)}
                    className="h-6 w-6 p-0 hover:bg-amber-100/50"
                  >
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("view_origin_engagement") || "View origin engagement"}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightCard;
